import {
  normalizeEventPerformanceTypeForThread,
  resolveExplicitEventChatMode,
  resolveExplicitEventGroupCount,
} from '../eventlist/eventThreadMetaUtils';

const toFiniteNumberOrNull = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export const buildRealtimeEventSidebarPatch = ({
  row,
  previousRow,
  nowJstDbString,
  resolveEventThreadId,
  resolveEventChatMode,
  resolveEventGroupCount,
  includeAssignedAdmins = false,
  includeUnreadCountFromRaw = false,
} = {}) => {
  const baseRow = row && typeof row === 'object' ? row : {};
  const prev = previousRow && typeof previousRow === 'object' ? previousRow : null;
  const fallbackTimestamp =
    typeof nowJstDbString === 'function' ? nowJstDbString() : '';

  const unreadFromRow = toFiniteNumberOrNull(baseRow.unreadCount);
  const unreadFromRaw = includeUnreadCountFromRaw
    ? toFiniteNumberOrNull(baseRow.unread_count)
    : null;
  const unreadFromPrev = toFiniteNumberOrNull(prev?.unreadCount);

  const patch = {
    event_uuid: baseRow.event_uuid,
    name: baseRow.name ?? (prev ? prev.name : '(イベント)'),
    flyer_front_url: baseRow.flyer_front_url ?? (prev ? prev.flyer_front_url : undefined),
    last_message_preview: baseRow.last_message_preview ?? (prev ? prev.last_message_preview : ''),
    last_message_time: baseRow.last_message_time ?? (prev ? prev.last_message_time : fallbackTimestamp),
    last_message_format: baseRow.last_message_format ?? (prev ? prev.last_message_format : undefined),
    unreadCount: unreadFromRow ?? unreadFromRaw ?? unreadFromPrev ?? 0,
    chat_thread_id:
      resolveEventThreadId?.(baseRow) ||
      (prev ? resolveEventThreadId?.(prev) : null),
    chat_mode:
      resolveExplicitEventChatMode(baseRow) ??
      resolveExplicitEventChatMode(prev) ??
      'separate',
    group_event_count:
      resolveExplicitEventGroupCount(baseRow) ??
      resolveExplicitEventGroupCount(prev) ??
      1,
    group_root_event_uuid:
      baseRow.group_root_event_uuid ??
      baseRow.groupRootEventUuid ??
      (prev ? prev.group_root_event_uuid : baseRow.event_uuid),
    representative_event_uuid:
      baseRow.representative_event_uuid ??
      baseRow.representativeEventUuid ??
      (prev ? prev.representative_event_uuid : baseRow.event_uuid),
    source_event_name:
      baseRow.source_event_name ??
      (prev ? prev.source_event_name : null),
    source_performance_type:
      baseRow.source_performance_type ??
      (prev ? prev.source_performance_type : null),
    source_event_id:
      baseRow.source_event_id ??
      (prev ? prev.source_event_id : null),
  };
  patch.performance_type = normalizeEventPerformanceTypeForThread(
    patch,
    baseRow.performance_type ?? (prev ? prev.performance_type : undefined)
  );

  if (includeAssignedAdmins) {
    patch.assigned_admins =
      baseRow.assigned_admins ??
      baseRow.assignedAdmins ??
      (prev ? prev.assigned_admins : undefined);
  }

  return patch;
};

export const upsertEventSidebarRowByUuid = ({
  prevEvents,
  eventUuid,
  patch,
  moveToTop = true,
  orderFrozen = false,
} = {}) => {
  const list = Array.isArray(prevEvents) ? prevEvents : [];
  const targetEventUuid = String(eventUuid || '').trim();
  if (!targetEventUuid || !patch || typeof patch !== 'object') return list;

  const idx = list.findIndex(
    (eventRow) => eventRow && String(eventRow.event_uuid || '') === targetEventUuid
  );
  const holdOrder = orderFrozen || !moveToTop;

  if (holdOrder) {
    if (idx >= 0) {
      const copy = list.slice();
      copy[idx] = { ...list[idx], ...patch };
      return copy;
    }
    return [...list, patch];
  }

  if (idx >= 0) {
    const others = list.slice(0, idx).concat(list.slice(idx + 1));
    return [{ ...list[idx], ...patch }, ...others];
  }
  return [patch, ...list];
};
